import { MediaTimeoutError, withTimeout } from "./with-timeout";

export type MediaKind = "microphone" | "camera";
export interface DeviceReadiness {
  permission: PermissionState | "unknown";
  selectedDevice?: string;
  lastCheck?: number;
  acquired: boolean;
  reason?: string;
}
interface DeviceController {
  listAudioInputDevices(): Promise<MediaDeviceInfo[]>;
  listVideoInputDevices(): Promise<MediaDeviceInfo[]>;
  startAudioInput(id: string | MediaTrackConstraints): Promise<MediaStream | undefined>;
  startVideoInput(id: string | MediaTrackConstraints): Promise<MediaStream | undefined>;
  stopAudioInput(): Promise<void>;
  stopVideoInput(): Promise<void>;
  destroy(): Promise<void>;
}
export interface MediaCapture<C> {
  controller: C;
  microphone?: Promise<MediaStream | undefined>;
  camera: Promise<boolean>;
  cameraStarted: boolean;
  disposed: boolean;
  previewReady: boolean;
  published: boolean;
  enabled: boolean;
  pending: boolean;
  meetingStarted: boolean;
  streams: Partial<Record<MediaKind, MediaStream>>;
}
interface Environment {
  permission(kind: MediaKind): Promise<PermissionState | "unknown">;
  devices(): Promise<MediaDeviceInfo[]>;
}
export const browserMediaEnvironment: Environment = {
  async permission(kind) {
    try {
      return (await navigator.permissions.query({ name: kind as PermissionName })).state;
    } catch {
      return "unknown";
    }
  },
  devices: async () => navigator.mediaDevices?.enumerateDevices() ?? [],
};

/** One app owns local inputs. Claim transfers cleanup authority to the call. */
export function createMediaReadiness<C extends DeviceController>(
  createController: () => C,
  environment: Environment = browserMediaEnvironment,
  report: (event: string, metrics?: Record<string, number>) => void = () => {},
  timeoutMs = 10_000,
  preparationLifetimeMs = 60_000,
  now: () => number = () => performance.now(),
) {
  const emit = (event: string, metrics?: Record<string, number>) => {
    try {
      report(event, metrics);
    } catch {
      /* Diagnostics must never affect capture. */
    }
  };
  let permissionCheckedAt: number | undefined;
  let cameraAcquisitionId = 0;
  const pendingCamera = new Set<{ target: MediaCapture<C>; cancel: () => void }>();
  const readiness: Record<MediaKind, DeviceReadiness> = {
    microphone: { permission: "unknown", acquired: false },
    camera: { permission: "unknown", acquired: false },
  };
  let capture: MediaCapture<C> | undefined;
  let claimed = false;
  let generation = 0;
  let preparation: Promise<void> | undefined;
  let expiry: ReturnType<typeof setTimeout> | undefined;
  let checking: Promise<void> | undefined;
  let deferredRelease:
    | { target: MediaCapture<C>; timer: ReturnType<typeof setTimeout>; finish: () => void }
    | undefined;
  function retain(target: MediaCapture<C>) {
    if (deferredRelease?.target !== target) return;
    clearTimeout(deferredRelease.timer);
    deferredRelease = undefined;
  }
  function deferRelease(target: MediaCapture<C>, beforeRelease: () => void) {
    retain(target);
    const finish = () => {
      retain(target);
      beforeRelease();
      release(target);
    };
    deferredRelease = { target, finish, timer: setTimeout(finish, 0) };
  }
  const kinds = ["microphone", "camera"] as const;
  const safely = (operation: () => Promise<void>) => {
    try {
      void operation().catch(() => {});
    } catch {
      /* Independent releases. */
    }
  };
  function release(target: MediaCapture<C>) {
    if (target.disposed) return;
    target.disposed = true;
    for (const pending of pendingCamera) if (pending.target === target) pending.cancel();
    for (const stream of Object.values(target.streams))
      stream.getTracks().forEach((track) => {
        track.stop();
      });
    safely(() => target.controller.stopAudioInput());
    safely(() => target.controller.stopVideoInput());
    safely(() => target.controller.destroy());
    if (capture === target) {
      capture = undefined;
      preparation = undefined;
      claimed = false;
      for (const kind of kinds) readiness[kind].acquired = false;
      clearTimeout(expiry);
    }
    emit("media.cleanup");
  }
  function cancelPreparation() {
    generation++;
    preparation = undefined;
    if (!claimed && capture) release(capture);
    emit("media.preparationCancelled");
  }
  function check(): Promise<void> {
    if (checking) return checking;
    checking = (async () => {
      const checkedAt = now();
      const [permissions, devices] = await Promise.all([
        Promise.all(
          kinds.map((kind) =>
            withTimeout(environment.permission(kind), timeoutMs, "Permission").catch(() => "unknown" as const),
          ),
        ),
        withTimeout(environment.devices(), timeoutMs, "Devices").catch(() => undefined),
      ]);
      permissionCheckedAt = checkedAt;
      let invalid = false;
      kinds.forEach((kind, index) => {
        const state = readiness[kind];
        state.permission = permissions[index] ?? "unknown";
        state.lastCheck = Date.now();
        const deviceKind = kind === "camera" ? "videoinput" : "audioinput";
        const missing =
          devices &&
          state.selectedDevice &&
          !devices.some((device) => device.kind === deviceKind && device.deviceId === state.selectedDevice);
        if (missing || state.permission === "denied") {
          state.reason = missing ? "Device disconnected" : "Permission denied";
          state.acquired = false;
          if (capture && (kind === "microphone" ? capture.microphone : capture.cameraStarted)) invalid = true;
        }
      });
      if (invalid) cancelPreparation();
      emit("media.checked");
    })().finally(() => {
      checking = undefined;
    });
    return checking;
  }
  function getCapture() {
    if (!capture) {
      capture = {
        controller: createController(),
        camera: Promise.resolve(false),
        cameraStarted: false,
        disposed: false,
        previewReady: false,
        published: false,
        enabled: true,
        pending: false,
        meetingStarted: false,
        streams: {},
      };
    }
    return capture;
  }
  function acquire(target: MediaCapture<C>, kind: MediaKind, explicit: boolean): Promise<MediaStream | undefined> {
    let expired = false;
    const controller = target.controller;
    const camera = kind === "camera";
    const id = camera ? ++cameraAcquisitionId : 0;
    const started = now();
    let stageStarted = started;
    let stage = "enumeration";
    let finished = false;
    let stageFinished = false;
    const pending = {
      target,
      cancel: () => {
        completeStage(5);
        finish(5);
      },
    };
    const duration = (start: number) => Math.max(0, Math.round(now() - start));
    const completeStage = (failureCategory: number) => {
      if (!camera || finished || stageFinished) return;
      stageFinished = true;
      emit(`media.camera.${stage}Completed`, {
        cameraAcquisitionId: id,
        durationMs: duration(stageStarted),
        failureCategory,
      });
    };
    const finish = (failureCategory: number) => {
      if (!camera || finished) return;
      finished = true;
      pendingCamera.delete(pending);
      emit(failureCategory ? "media.camera.failed" : "media.camera.prepared", {
        cameraAcquisitionId: id,
        durationMs: duration(started),
        ...(failureCategory ? { failureCategory } : {}),
      });
    };
    if (camera) {
      const permission =
        permissionCheckedAt !== undefined && now() - permissionCheckedAt <= 60_000
          ? readiness.camera.permission
          : "unknown";
      emit("media.camera.acquisitionStarted", {
        cameraAcquisitionId: id,
        acquisitionContext: explicit ? 1 : 0,
        permissionState: { unknown: 0, granted: 1, prompt: 2, denied: 3 }[permission],
      });
      pendingCamera.add(pending);
    }
    const work = (async () => {
      const devices = await (kind === "camera"
        ? controller.listVideoInputDevices()
        : controller.listAudioInputDevices());
      if (target.disposed || expired) return;
      completeStage(0);
      const selected = devices.find((device) => device.deviceId === readiness[kind].selectedDevice) ?? devices[0];
      // Browsers can grant permission while still hiding IDs until first capture.
      // An empty constraints object selects a real default input; an empty string
      // selects the SDK's silent fallback. Background callers were permission-gated.
      if (!explicit && readiness[kind].permission !== "granted") {
        finish(5);
        return;
      }
      readiness[kind].selectedDevice = selected?.deviceId || undefined;
      const input = selected?.deviceId || {};
      stage = "input";
      stageFinished = false;
      stageStarted = now();
      const stream = await (kind === "camera" ? controller.startVideoInput(input) : controller.startAudioInput(input));
      if (!target.disposed && !expired) completeStage(0);
      if (!stream) throw new DeviceUnavailableError(`${kind} unavailable`);
      if (target.disposed || expired) {
        stream.getTracks().forEach((track) => {
          track.stop();
        });
        return;
      }
      const track = stream.getTracks()[0];
      if (!track || track.readyState !== "live") {
        stream.getTracks().forEach((track) => {
          track.stop();
        });
        throw new DeviceUnavailableError(`${kind} track unavailable`);
      }
      target.streams[kind] = stream;
      readiness[kind].selectedDevice = track.getSettings().deviceId ?? readiness[kind].selectedDevice;
      Object.assign(readiness[kind], { acquired: true, lastCheck: Date.now(), reason: undefined });
      track.addEventListener(
        "ended",
        () => {
          if (capture !== target || target.disposed || target.streams[kind] !== stream) return;
          readiness[kind].acquired = false;
          readiness[kind].reason = "Device disconnected";
          cancelPreparation();
        },
        { once: true },
      );
      if (camera) finish(0);
      else emit(`media.${kind}.prepared`);
      return stream;
    })();
    return withTimeout(work, timeoutMs, kind).catch((error: unknown) => {
      expired = true;
      if (!target.disposed) {
        readiness[kind].acquired = false;
        readiness[kind].reason = error instanceof Error ? error.message : "Capture failed";
        readiness[kind].lastCheck = Date.now();
      }
      if (camera) {
        const category = target.disposed ? 5 : cameraFailureCategory(error);
        completeStage(category);
        finish(category);
      } else emit(`media.${kind}.failed`);
      throw error;
    });
  }
  function startInputs(target: MediaCapture<C>, explicit: boolean) {
    if (!target.microphone && (explicit || readiness.microphone.permission === "granted")) {
      target.microphone = acquire(target, "microphone", explicit);
      // A prepared microphone may fail before there is a call to consume it.
      void target.microphone.catch(() => {});
    }
    if (!target.cameraStarted && (explicit || readiness.camera.permission === "granted")) {
      target.cameraStarted = true;
      target.pending = true;
      target.camera = acquire(target, "camera", explicit)
        .then((stream) => {
          target.previewReady = Boolean(stream);
          return Boolean(stream);
        })
        .catch(() => {
          target.enabled = false;
          return false;
        })
        .finally(() => {
          target.pending = false;
        });
    }
  }
  function prepare(): Promise<void> {
    if (claimed) return Promise.resolve();
    if (preparation) return preparation;
    const version = generation;
    preparation = (async () => {
      await check();
      if (version !== generation || claimed) return;
      if (!kinds.some((kind) => readiness[kind].permission === "granted")) return;
      const target = getCapture();
      emit("media.preparationStarted");
      startInputs(target, false);
      expiry = setTimeout(cancelPreparation, preparationLifetimeMs);
      await Promise.allSettled([target.microphone, target.camera]);
    })();
    return preparation;
  }
  function setCameraEnabled(target: MediaCapture<C>, enabled: boolean): Promise<boolean> {
    target.enabled = enabled;
    target.pending = true;
    target.camera = (async () => {
      if (!enabled) {
        target.previewReady = false;
        target.published = false;
        target.streams.camera?.getTracks().forEach((track) => {
          track.stop();
        });
        delete target.streams.camera;
        readiness.camera.acquired = false;
        await withTimeout(target.controller.stopVideoInput(), timeoutMs, "Camera release");
        return false;
      }
      const stream = await acquire(target, "camera", true);
      target.previewReady = Boolean(stream);
      return Boolean(stream);
    })()
      .catch(() => {
        target.enabled = false;
        target.previewReady = false;
        return false;
      })
      .finally(() => {
        target.pending = false;
      });
    return target.camera;
  }
  function claim(): MediaCapture<C> {
    deferredRelease?.finish();
    if (claimed) throw new Error("Media devices already belong to a call");
    // Synchronous transfer also wins against a preparation still checking permission.
    generation++;
    clearTimeout(expiry);
    const target = getCapture();
    claimed = true;
    startInputs(target, true);
    emit("media.claimed");
    return target;
  }
  return {
    check,
    prepare,
    claim,
    cancelPreparation,
    release,
    deferRelease,
    retain,
    setCameraEnabled,
    snapshot: () => ({
      microphone: { ...readiness.microphone },
      camera: { ...readiness.camera },
      speaker: { audibleOutputVerified: false as const },
    }),
  };
}

/** Both initial startup and a setup-time camera toggle use this ordering guard. */
export function publishPreparedCamera<C>(capture: MediaCapture<C>, publish: () => void): boolean {
  if (!capture.meetingStarted || !capture.previewReady || !capture.enabled || capture.disposed || capture.published)
    return false;
  publish();
  capture.published = true;
  return true;
}

class DeviceUnavailableError extends Error {}

/** Only error names/classes are inspected; SDK messages and nested objects never leave capture. */
function cameraFailureCategory(error: unknown): number {
  if (error instanceof MediaTimeoutError) return 3;
  if (error instanceof DeviceUnavailableError) return 2;
  try {
    const name = error && typeof error === "object" && "name" in error ? error.name : undefined;
    if (name === "NotAllowedError" || name === "PermissionDeniedError" || name === "SecurityError") return 1;
    if (
      ["NotFoundError", "NotReadableError", "OverconstrainedError", "DeviceNotFoundError", "TrackStartError"].includes(
        name as string,
      )
    )
      return 2;
    if (name === "TimeoutError") return 3;
  } catch {
    /* Host objects may throw on property access. */
  }
  return 4;
}
